import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, BookOpen, Clock, Award, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AcademyCertVerifyPage() {
  const { code } = useParams();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!code) return;
    fetch(`${API}/academy/certificates/${code.toUpperCase()}/verify`)
      .then(r => {
        if (!r.ok) throw new Error('not found');
        return r.json();
      })
      .then(data => setCert(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [code]);

  if (loading) return (
    <div className="min-h-screen bg-[#030712] flex items-center justify-center">
      <div className="w-10 h-10 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#030712] flex flex-col items-center justify-center px-4 py-12" data-testid="cert-verify-page">
      <div className="w-full max-w-lg space-y-6">
        {/* Header */}
        <div className="text-center">
          <Link to="/academy" className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white text-sm mb-4 transition">
            <BookOpen size={14} /> Neuralia369 Academy
          </Link>
          <h1 className="text-2xl font-black text-white">Verificación de Certificado</h1>
          <p className="text-[#94A3B8] text-sm mt-1">Código: <span className="font-mono text-white">{code?.toUpperCase()}</span></p>
        </div>

        {notFound ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-8 text-center space-y-4" data-testid="cert-invalid">
            <XCircle size={48} className="text-red-400 mx-auto" />
            <div>
              <p className="text-xl font-bold text-white">Certificado no encontrado</p>
              <p className="text-[#94A3B8] text-sm mt-1">Este código no corresponde a ningún certificado válido.</p>
            </div>
            <Link to="/academy" className="inline-block px-5 py-2.5 rounded-xl border border-[rgba(148,163,184,0.14)] text-[#94A3B8] hover:text-white hover:border-white/20 transition text-sm">
              Ir al catálogo
            </Link>
          </div>
        ) : cert ? (
          <div className="space-y-4" data-testid="cert-valid">
            {/* Status banner */}
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-4 flex items-center gap-3">
              <CheckCircle size={24} className="text-emerald-400 flex-shrink-0" />
              <div>
                <p className="text-emerald-400 font-bold">Certificado válido</p>
                <p className="text-xs text-[#94A3B8]">Este certificado ha sido verificado y es auténtico</p>
              </div>
            </div>

            {/* Certificate card */}
            <div className="bg-[rgba(8,15,32,0.92)] border border-[rgba(148,163,184,0.14)] rounded-2xl overflow-hidden">
              {/* Visual header */}
              <div className="bg-gradient-to-br from-indigo-700 to-indigo-900 p-6 text-center space-y-3">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mx-auto">
                  <Award size={28} className="text-white" />
                </div>
                <div>
                  <p className="text-white/70 text-xs uppercase tracking-widest">Neuralia369 Academy certifica que</p>
                  <p className="text-2xl font-black text-white mt-1">{cert.display_name}</p>
                </div>
                <p className="text-white/80 text-sm">ha completado satisfactoriamente</p>
                <p className="text-xl font-bold text-white">{cert.course_name}</p>
              </div>

              {/* Details */}
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center bg-white/5 rounded-xl p-3">
                    <Clock size={16} className="text-indigo-400 mx-auto mb-1" />
                    <p className="text-white font-bold text-sm">{cert.hours}h</p>
                    <p className="text-[10px] text-[#94A3B8]">Horas</p>
                  </div>
                  <div className="text-center bg-white/5 rounded-xl p-3">
                    <CheckCircle size={16} className="text-emerald-400 mx-auto mb-1" />
                    <p className="text-white font-bold text-sm">100%</p>
                    <p className="text-[10px] text-[#94A3B8]">Completado</p>
                  </div>
                  <div className="text-center bg-white/5 rounded-xl p-3">
                    <BookOpen size={16} className="text-amber-400 mx-auto mb-1" />
                    <p className="text-white font-bold text-sm">{cert.issued_date}</p>
                    <p className="text-[10px] text-[#94A3B8]">Emitido</p>
                  </div>
                </div>

                <div className="border-t border-[rgba(148,163,184,0.14)] pt-4 space-y-2 text-xs text-[#94A3B8]">
                  <div className="flex justify-between">
                    <span>Código de verificación</span>
                    <span className="font-mono text-white">{cert.code}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Institución</span>
                    <span className="text-white">Neuralia369 Academy</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>URL pública</span>
                    <a href={`https://neuralia369.com/academy/verificar/${cert.code}`}
                      target="_blank" rel="noreferrer"
                      className="text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
                      Ver <ExternalLink size={11} />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-center text-xs text-[#94A3B8]">
              Este certificado puede ser verificado en cualquier momento en{' '}
              <span className="text-white">neuralia369.com/academy/verificar/{cert.code}</span>
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
